import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';

import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { Tag } from 'dashboard/models/tag';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import { CaseAnalyticsFilterDateType } from 'dashboard/redux/actions/case-analytics';
import { CaseAnalyticsState } from 'dashboard/redux/reducers/case-analytics';
import CaseAssignmentDistributionCard from 'dashboard/views/DashboardCaseAnalyticsProductivityView/CaseAssignmentDistributionCard';
import CaseResolutionCard from 'dashboard/views/DashboardCaseAnalyticsProductivityView/CaseResolutionCard';
import CaseStatusCard from 'dashboard/views/DashboardCaseAnalyticsProductivityView/CaseStatusCard';
import { CaseAnalyticsFilters } from 'dashboard/views/DashboardCaseAnalyticsView';
import { Iso8601DateString } from 'lib/types';

export interface DashboardCaseAnalyticsProductivityViewProps
  extends Pick<
    CaseAnalyticsState,
    | 'caseStatusOverTime'
    | 'caseResolutionTimes'
    | 'caseAssignmentDistribution'
    | 'filters'
    | 'updatedAt'
  > {
  activeCaseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  tags: Tag[];
  users: User[];
  teams: Team[];
  queriedAt: Iso8601DateString;
  loadingIntervals: boolean;
  loadingResolutionTimes: boolean;
  loadingAssignmentDistribution: boolean;
  loading: boolean;
  loadProductivityAnalyticsIntervals: () => Promise<void>;
  loadProductivityAnalyticsResolutionTimes: () => Promise<void>;
  loadProductivityAnalyticsAssignmentDistribution: () => Promise<void>;
  handleDateTypeUpdate: (type: CaseAnalyticsFilterDateType) => void;
  handleCaseTemplateFilterUpdate: (caseTemplateIds: string[]) => void;
  handleCaseTagFilterUpdate: (caseTagIds: string[]) => void;
  handleAssigneeFilterUpdate: (userAssigneeIds: string[], teamAssigneeIds: string[]) => void;
  handleResolverFilterUpdate: (userResolverIds: string[], teamResolverIds: string[]) => void;
  handleStatusFilterChange: (stateIds: string[]) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
}

const DashboardCaseAnalyticsProductivityView = ({
  caseStatusOverTime,
  caseResolutionTimes,
  caseAssignmentDistribution,
  activeCaseTemplateBasicsOptions,
  tags,
  users,
  teams,
  queriedAt,
  updatedAt,
  filters,
  loadingIntervals,
  loadingResolutionTimes,
  loadingAssignmentDistribution,
  loading,
  loadProductivityAnalyticsIntervals,
  loadProductivityAnalyticsResolutionTimes,
  loadProductivityAnalyticsAssignmentDistribution,
  handleDateUpdateV2,
  handleDateTypeUpdate,
  handleCaseTemplateFilterUpdate,
  handleCaseTagFilterUpdate,
  handleAssigneeFilterUpdate,
  handleResolverFilterUpdate,
  handleStatusFilterChange,
}: DashboardCaseAnalyticsProductivityViewProps) => {
  const allStates = activeCaseTemplateBasicsOptions.flatMap((ct) => ct.states);

  const loadProductivityAnalytics = () => {
    loadProductivityAnalyticsIntervals();
    loadProductivityAnalyticsResolutionTimes();
    loadProductivityAnalyticsAssignmentDistribution();
  };

  return (
    <>
      <CaseAnalyticsFilters
        activeCaseTemplateBasicsOptions={activeCaseTemplateBasicsOptions}
        tags={tags}
        users={users}
        teams={teams}
        queriedAt={queriedAt}
        updatedAt={updatedAt}
        filters={filters}
        loading={loading}
        loadAnalytics={loadProductivityAnalytics}
        handleDateUpdateV2={handleDateUpdateV2}
        handleDateTypeUpdate={handleDateTypeUpdate}
        handleCaseTemplateFilterUpdate={handleCaseTemplateFilterUpdate}
        handleCaseTagFilterUpdate={handleCaseTagFilterUpdate}
        handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
        handleResolverFilterUpdate={handleResolverFilterUpdate}
        handleStatusFilterChange={handleStatusFilterChange}
      />
      <CaseStatusCard
        loading={loadingIntervals}
        caseTemplateBasicsOptions={activeCaseTemplateBasicsOptions}
        allStates={allStates}
        {...caseStatusOverTime}
      />
      <CaseResolutionCard loading={loadingResolutionTimes} {...caseResolutionTimes} />
      <CaseAssignmentDistributionCard
        loading={loadingAssignmentDistribution}
        users={users}
        allStates={allStates}
        {...caseAssignmentDistribution}
      />
    </>
  );
};

export default DashboardCaseAnalyticsProductivityView;
